(() => {
  if (window.WVHeaderParity) { window.WVHeaderParity.init(document); return; }
  const instances = new WeakMap();
  const roots = scope => [...(scope.matches?.('[data-wvrh-header]') ? [scope] : []), ...scope.querySelectorAll('[data-wvrh-header]')];
  const init = scope => roots(scope).forEach(header => {
    if (instances.has(header)) return;
    const events = new AbortController();
    const signal = events.signal;
    const timers = new Set();
    const desktop = matchMedia('(min-width: 1000px)');
    const hoverDesktop = matchMedia('(min-width: 1000px) and (hover: hover) and (pointer: fine)');
    const delay = (callback, time) => { const timer = setTimeout(() => { timers.delete(timer); callback(); }, time); timers.add(timer); return timer; };
    const searchRoot = header.querySelector('[data-wvrh-search-root]');
    const form = header.querySelector('[data-wvrh-predictive]');
    const input = form.querySelector('[name="q"]');
    const results = form.querySelector('[data-wvrh-results]');
    const shortcuts = form.querySelector('[data-wvrh-shortcuts]');
    const options = form.querySelector('[data-wvrh-options]');
    const status = form.querySelector('[data-wvrh-status]');
    const all = form.querySelector('[data-wvrh-all]');
    const toggle = header.querySelector('[data-wvrh-search-toggle]');
    const closeButton = header.querySelector('[data-wvrh-search-close]');
    const disclosures = [...header.querySelectorAll('[data-wvrh-disclosure]')];
    const hoverMenus = [...header.querySelectorAll('.wvrh-desktop-nav .wvrh-category[data-wvrh-disclosure]')];
    const english = form.dataset.english === 'true';
    let request;
    let revision = 0;
    let debounce;
    let active = -1;
    let restoringSearchFocus = false;
    let hoverOpenTimer;
    let hoverCloseTimer;
    let hoveredMenu = null;
    const cancelMenuHover = () => {
      [hoverOpenTimer, hoverCloseTimer].forEach(timer => { clearTimeout(timer); timers.delete(timer); });
      hoverOpenTimer = undefined;
      hoverCloseTimer = undefined;
    };
    const containsKeyboardFocus = container => document.activeElement instanceof Element && container.contains(document.activeElement) && document.activeElement.matches(':focus-visible');
    const cancel = () => { revision++; clearTimeout(debounce); timers.delete(debounce); request?.abort(); };
    const hideResults = () => { results.hidden = true; input.setAttribute('aria-expanded', 'false'); input.removeAttribute('aria-activedescendant'); active = -1; };
    const closeSearch = restore => {
      cancel();
      hideResults();
      if (shortcuts) shortcuts.hidden = true;
      searchRoot.open = desktop.matches;
      searchRoot.removeAttribute('data-search-open');
      toggle.setAttribute('aria-expanded', String(searchRoot.open));
      if (restore) {
        restoringSearchFocus = true;
        (desktop.matches ? input : toggle).focus({preventScroll: true});
        restoringSearchFocus = false;
      }
    };
    const closeDisclosure = (details, restore = false) => {
      details.open = false;
      details.querySelectorAll('details[open]').forEach(nested => { nested.open = false; });
      const shoppingMenu = details.closest('[data-wv-shopping-menu]');
      shoppingMenu?.classList.toggle('wv-shop-drilled', !!shoppingMenu.querySelector('details[open]'));
      if (restore) details.querySelector('summary').focus({preventScroll: true});
    };
    const closeOthers = current => {
      disclosures.forEach(other => {
        if (other.open && other !== current && !other.contains(current) && !current.contains(other)) closeDisclosure(other);
      });
      if (current !== searchRoot && searchRoot.open) closeSearch(false);
    };
    hoverMenus.forEach(details => {
      details.addEventListener('pointerenter', event => {
        if (!hoverDesktop.matches || event.pointerType === 'touch') return;
        cancelMenuHover();
        hoveredMenu = details;
        if (details.open) return;
        hoverOpenTimer = delay(() => {
          if (!hoverDesktop.matches || hoveredMenu !== details || !header.isConnected) return;
          if (hoverMenus.some(other => other !== details && containsKeyboardFocus(other))) return;
          closeOthers(details);
          details.open = true;
        }, 160);
      }, {signal});
      details.addEventListener('pointerleave', event => {
        if (!hoverDesktop.matches || event.pointerType === 'touch') return;
        cancelMenuHover();
        hoveredMenu = null;
        hoverCloseTimer = delay(() => {
          if (!hoverDesktop.matches || hoveredMenu) return;
          hoverMenus.forEach(menu => {
            if (menu.open && !containsKeyboardFocus(menu)) closeDisclosure(menu);
          });
        }, 250);
      }, {signal});
      details.querySelectorAll('.wvrh-submenu[data-wvrh-category-menu]').forEach(menu => {
        const triggers = [...menu.querySelectorAll('a[data-wvrh-category-trigger]')];
        const panels = [...menu.querySelectorAll('div[data-wvrh-category-panel]')];
        const activatePanel = (trigger, fromPointer) => {
          const panelId = trigger.dataset.wvrhCategoryTrigger;
          const target = panels.find(panel => panel.dataset.wvrhCategoryPanel === panelId && panel.id === panelId);
          if (!desktop.matches || !target) return;
          if (fromPointer && panels.some(panel => panel !== target && containsKeyboardFocus(panel))) return;
          panels.forEach(panel => { panel.hidden = panel !== target; });
          triggers.forEach(link => {
            if (link === trigger) link.setAttribute('data-active', 'true');
            else link.removeAttribute('data-active');
          });
        };
        triggers.forEach(trigger => {
          trigger.addEventListener('pointerenter', event => {
            if (hoverDesktop.matches && event.pointerType !== 'touch') activatePanel(trigger, true);
          }, {signal});
          trigger.addEventListener('focus', () => activatePanel(trigger, false), {signal});
        });
      });
    });
    const linkedMenus = [...header.querySelectorAll('[data-wv-linked-menu]')];
    linkedMenus.forEach(menu => {
      const groups = [...menu.querySelectorAll('[data-wv-nav-group]')];
      let groupTimer;
      const cancelGroupHover = () => {
        clearTimeout(groupTimer);
        timers.delete(groupTimer);
      };
      const activateGroup = (target, fromPointer = false) => {
        if (fromPointer && groups.some(group => group !== target && containsKeyboardFocus(group.querySelector('[data-wv-nav-content]')))) return;
        cancelGroupHover();
        groups.forEach(group => {
          const active = group === target;
          group.toggleAttribute('data-active', active);
          group.querySelector('[data-wv-nav-content]').hidden = !active;
          group.querySelector('[data-wv-nav-toggle]').setAttribute('aria-expanded', String(active));
        });
      };
      groups.forEach(group => {
        const row = group.querySelector('.wv-linked-row');
        const trigger = group.querySelector('[data-wv-nav-trigger]');
        const button = group.querySelector('[data-wv-nav-toggle]');
        const content = group.querySelector('[data-wv-nav-content]');
        button.hidden = false;
        row.addEventListener('pointerenter', event => {
          if (!hoverDesktop.matches || event.pointerType === 'touch') return;
          cancelGroupHover();
          groupTimer = delay(() => activateGroup(group, true), 100);
        }, {signal});
        row.addEventListener('pointerleave', cancelGroupHover, {signal});
        row.addEventListener('focusin', () => {
          if (desktop.matches) activateGroup(group);
        }, {signal});
        button.addEventListener('click', () => {
          activateGroup(!desktop.matches && group.hasAttribute('data-active') ? null : group);
          if (desktop.matches) content.querySelector('a')?.focus({preventScroll: true});
        }, {signal});
        row.addEventListener('keydown', event => {
          if (event.key !== 'ArrowRight') return;
          event.preventDefault();
          activateGroup(group);
          content.querySelector('a')?.focus({preventScroll: true});
        }, {signal});
        content.addEventListener('keydown', event => {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            trigger.focus({preventScroll: true});
          } else if (event.key === 'Escape' && !desktop.matches) {
            event.preventDefault();
            event.stopPropagation();
            activateGroup(null);
            button.focus({preventScroll: true});
          }
        }, {signal});
      });
      menu.addEventListener('pointerleave', cancelGroupHover, {signal});
      menu.closest('details').addEventListener('toggle', event => {
        if (!event.target.open) activateGroup(desktop.matches ? groups[0] : null);
      }, {signal});
      desktop.addEventListener('change', () => activateGroup(desktop.matches ? groups[0] : null), {signal});
      activateGroup(desktop.matches ? groups[0] : null);
      menu.setAttribute('data-wv-linked-ready', '');
    });
    if (hoverMenus.length) {
      header.addEventListener('focusin', event => {
        if (event.target instanceof Element && event.target.matches(':focus-visible')) cancelMenuHover();
      }, {signal});
      document.addEventListener('keydown', event => {
        if (event.key !== 'Escape') return;
        cancelMenuHover();
        hoveredMenu = null;
        hoverMenus.forEach(menu => {
          if (!menu.open) return;
          event.preventDefault();
          closeDisclosure(menu, menu.contains(document.activeElement));
        });
      }, {signal, capture: true});
      hoverDesktop.addEventListener('change', () => {
        cancelMenuHover();
        hoveredMenu = null;
        if (!hoverDesktop.matches && desktop.matches) {
          hoverMenus.forEach(menu => {
            if (menu.open) closeDisclosure(menu, menu.contains(document.activeElement));
          });
        }
      }, {signal});
    }
    header.querySelectorAll('[data-wv-menu-back]').forEach(button => {
      button.hidden = false;
      button.addEventListener('click', () => closeDisclosure(button.closest('details'), true), {signal});
    });
    header.querySelectorAll('[data-wvrh-mobile] [data-wv-shopping-menu] details').forEach(details => {
      details.addEventListener('toggle', () => {
        const menu = details.closest('[data-wv-shopping-menu]');
        menu.classList.toggle('wv-shop-drilled', !!menu.querySelector('details[open]'));
        if (details.open && !desktop.matches) details.querySelector('[data-wv-menu-back]').focus({preventScroll: true});
      }, {signal});
    });
    closeButton.hidden = false;
    searchRoot.open = desktop.matches;
    toggle.setAttribute('aria-expanded', String(searchRoot.open));
    toggle.addEventListener('click', event => {
      event.preventDefault();
      if (searchRoot.open) return closeSearch(true);
      closeOthers(searchRoot);
      searchRoot.open = true;
      searchRoot.setAttribute('data-search-open', '');
      toggle.setAttribute('aria-expanded', 'true');
      input.focus({preventScroll: true});
    }, {signal});
    searchRoot.addEventListener('toggle', () => {
      toggle.setAttribute('aria-expanded', String(searchRoot.open));
      if (!searchRoot.open) {
        cancel();
        hideResults();
        if (shortcuts) shortcuts.hidden = true;
        searchRoot.removeAttribute('data-search-open');
        if (searchRoot.contains(document.activeElement)) toggle.focus({preventScroll: true});
      }
    }, {signal});
    closeButton.addEventListener('click', () => closeSearch(true), {signal});
    searchRoot.addEventListener('keydown', event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        closeSearch(true);
      }
    }, {signal});
    const fullUrl = query => { const url = new URL(form.action); url.searchParams.set('q', query); url.searchParams.set('options[prefix]', 'last'); return url.href; };
    const safeUrl = value => { try { const url = new URL(value, location.origin); return url.origin === location.origin && /^https?:$/.test(url.protocol) ? url.href : null; } catch { return null; } };
    const show = message => { status.textContent = message; results.hidden = false; input.setAttribute('aria-expanded', 'true'); };
    const search = async query => {
      const current = revision;
      request = new AbortController();
      const pending = request;
      const timeout = delay(() => pending.abort(), 5000);
      options.replaceChildren(); active = -1;
      input.removeAttribute('aria-activedescendant');
      all.href = fullUrl(query);
      show(english ? 'Searching …' : 'Suche läuft …');
      try {
        const endpoint = new URL(form.dataset.predictiveUrl + '.json', location.origin);
        endpoint.searchParams.set('q', query);
        endpoint.searchParams.set('resources[type]', 'product,collection,page');
        endpoint.searchParams.set('resources[limit]', '6');
        const response = await fetch(endpoint, {signal: pending.signal, headers: {Accept: 'application/json'}});
        if (!response.ok) throw new Error('Search unavailable');
        const payload = await response.json();
        if (current !== revision || query !== input.value.trim()) return;
        const groups = payload.resources?.results;
        if (!groups) throw new Error('Invalid response');
        const entries = [...(groups.products || []), ...(groups.collections || []), ...(groups.pages || [])].slice(0, 6);
        entries.forEach(entry => {
          const href = safeUrl(entry.url);
          if (!href || !entry.title) return;
          const item = document.createElement('li'); item.setAttribute('role', 'presentation');
          const link = document.createElement('a'); link.className = 'wvrh-result'; link.href = href; link.id = `${input.id}-option-${options.children.length}`; link.setAttribute('role', 'option'); link.setAttribute('aria-selected', 'false');
          const imageSource = entry.featured_image?.url || entry.image;
          if (typeof imageSource === 'string') {
            try { const imageUrl = new URL(imageSource, location.origin); if (/^https?:$/.test(imageUrl.protocol)) { const image = document.createElement('img'); image.src = imageUrl.href; image.alt = ''; image.width = 56; image.height = 56; link.append(image); } } catch {}
          }
          const title = document.createElement('span'); title.textContent = entry.title; link.append(title); item.append(link); options.append(item);
        });
        const count = options.children.length;
        show(count ? `${count} ${english ? 'suggestions' : 'Suchvorschläge'}` : (english ? 'No suggestions. Try the full search.' : 'Keine Vorschläge. Nutze die vollständige Suche.'));
      } catch {
        if (current === revision) show(english ? 'Suggestions unavailable. Use the full search.' : 'Vorschläge nicht verfügbar. Nutze die vollständige Suche.');
      } finally { clearTimeout(timeout); timers.delete(timeout); }
    };
    const refreshSearch = () => {
      cancel();
      hideResults();
      const query = input.value.trim();
      if (shortcuts) shortcuts.hidden = !searchRoot.open || query.length >= 2;
      if (searchRoot.open && query.length >= 2) debounce = delay(() => search(query), 180);
    };
    input.addEventListener('input', refreshSearch, {signal});
    input.addEventListener('click', () => {
      if (shortcuts?.hidden && input.value.trim().length < 2) refreshSearch();
    }, {signal});
    input.addEventListener('focus', () => {
      if (restoringSearchFocus) return;
      closeOthers(searchRoot);
      refreshSearch();
    }, {signal});
    input.addEventListener('keydown', event => {
      const links = [...options.querySelectorAll('a')];
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); closeSearch(true); }
      else if (['ArrowDown', 'ArrowUp'].includes(event.key) && !results.hidden && links.length) {
        event.preventDefault(); active = active < 0 ? (event.key === 'ArrowDown' ? 0 : links.length - 1) : (active + (event.key === 'ArrowDown' ? 1 : -1) + links.length) % links.length;
        links.forEach((link, index) => link.setAttribute('aria-selected', String(index === active)));
        input.setAttribute('aria-activedescendant', links[active].id); links[active].scrollIntoView({block: 'nearest'});
      } else if (event.key === 'Enter' && active >= 0 && !results.hidden) { event.preventDefault(); links[active]?.click(); }
    }, {signal});
    form.addEventListener('submit', () => cancel(), {signal});

    searchRoot.addEventListener('focusout', event => {
      if (event.relatedTarget instanceof Node && !searchRoot.contains(event.relatedTarget)) closeSearch(false);
    }, {signal});
    header.querySelectorAll('[data-wvrh-close]').forEach(button => {
      button.hidden = false;
      button.addEventListener('click', () => closeDisclosure(button.closest('details'), true), {signal});
    });
    disclosures.forEach(details => {
      details.querySelector('summary').addEventListener('click', () => {
        if (!details.open) closeOthers(details);
      }, {signal});
      details.addEventListener('toggle', () => {
        if (details.open) closeOthers(details);
        else details.querySelectorAll('details[open]').forEach(nested => { nested.open = false; });
      }, {signal});
      details.addEventListener('focusout', event => {
        if (event.relatedTarget instanceof Node && !details.contains(event.relatedTarget)) closeDisclosure(details);
      }, {signal});
    });
    header.addEventListener('keydown', event => {
      if (event.key !== 'Escape' || !(event.target instanceof Element)) return;
      const current = event.target.closest('details[open]');
      if (!current || !header.contains(current)) return;
      event.preventDefault();
      event.stopPropagation();
      closeDisclosure(current, true);
    }, {signal});
    document.addEventListener('pointerdown', event => {
      cancelMenuHover();
      hoveredMenu = null;
      if (!(event.target instanceof Node)) return;
      if (searchRoot.open && !searchRoot.contains(event.target)) closeSearch(searchRoot.contains(document.activeElement));
      disclosures.forEach(details => {
        if (details.open && !details.contains(event.target)) closeDisclosure(details, details.contains(document.activeElement));
      });
    }, {signal});
    const closeAll = () => {
      cancelMenuHover();
      hoveredMenu = null;
      if (searchRoot.open) closeSearch(searchRoot.contains(document.activeElement));
      disclosures.forEach(details => { if (details.open) closeDisclosure(details, details.contains(document.activeElement)); });
    };
    header.querySelector('.js-drawer-open-cart')?.addEventListener('click', closeAll, {signal});
    document.addEventListener('cart:open', closeAll, {signal});
    desktop.addEventListener('change', () => {
      cancelMenuHover();
      hoveredMenu = null;
      const focused = document.activeElement;
      const focusInHeader = header.contains(focused);
      const searchFocused = searchRoot.contains(focused);
      const menuFocused = focusInHeader && focused.closest('nav, [data-wvrh-mobile], .wvrh-account');
      const disclosure = focusInHeader ? focused.closest('[data-wvrh-disclosure]') : null;
      closeSearch(false);
      disclosures.forEach(details => closeDisclosure(details));
      const target = searchFocused ? (desktop.matches ? input : toggle) : menuFocused
        ? (desktop.matches ? header.querySelector('.wvrh-logo') : header.querySelector('[data-wvrh-mobile] > summary'))
        : disclosure?.querySelector('summary');
      if (target) target.focus({preventScroll: true});
    }, {signal});
    header.setAttribute('data-wvrh-enhanced', '');
    instances.set(header, () => {
      cancel();
      cancelMenuHover();
      hoveredMenu = null;
      timers.forEach(clearTimeout);
      events.abort();
      closeSearch(false);
      disclosures.forEach(details => closeDisclosure(details));
      header.removeAttribute('data-wvrh-enhanced');
      linkedMenus.forEach(menu => {
        menu.removeAttribute('data-wv-linked-ready');
        menu.querySelectorAll('[data-wv-nav-content]').forEach(content => { content.hidden = false; });
        menu.querySelectorAll('[data-wv-nav-toggle]').forEach(button => { button.hidden = true; });
      });
      header.querySelectorAll('[data-wvrh-close], [data-wvrh-search-close]').forEach(button => { button.hidden = true; });
    });
  });
  const destroy = scope => roots(scope).forEach(header => { instances.get(header)?.(); instances.delete(header); });
  window.WVHeaderParity = {init, destroy};
  window.WVReferenceHeader = {initAll: init, destroyAll: destroy};
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => destroy(event.target));
  init(document);
})();
